<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import { useLocale } from '@/composables/useLocale'
import { useCreditBalance } from '@/composables/useCreditBalance'
import { useSupportUnreadCount } from '@/composables/useSupportUnreadCount'
import CreditChip from '@/components/organizer/newsletter/CreditChip.vue'
import OrganizerProfileMenu from '@/components/layout/OrganizerProfileMenu.vue'

const { t } = useI18n()
const router = useRouter()
const { logout, currentOrganisation } = useAuth()

// Single credit-balance fetch shared by the desktop chip and the mobile profile menu
const organisationId = currentOrganisation.value?.id ?? 0
const { balance, loading: balanceLoading, refresh: refreshBalance } = useCreditBalance(organisationId)

onMounted(() => {
  if (organisationId) {
    void refreshBalance()
  }
})
const { currentLocale, supportedLocales, switchLocale } = useLocale()

// Read from the module-level singleton — never fetch here to avoid per-render requests
const { hasUnread } = useSupportUnreadCount()

const isLangMenuOpen = ref(false)

const emit = defineEmits<{ (e: 'toggle-sidebar'): void }>()

async function handleLogout(): Promise<void> {
  try {
    await logout()
  } finally {
    void router.push('/organizer/login')
  }
}

function handleLanguageChange(locale: typeof supportedLocales[number]): void {
  switchLocale(locale)
  isLangMenuOpen.value = false
}
</script>

<template>
  <header class="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-30">
    <div class="px-4 sm:px-6">
      <div class="flex items-center justify-between h-16">
        <div class="flex items-center gap-3">
          <!-- Hamburger (mobile only) -->
          <button
            type="button"
            class="lg:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            :aria-label="t('nav.toggle_sidebar')"
            @click="emit('toggle-sidebar')"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <!-- Logo -->
          <RouterLink to="/organizer/dashboard" class="inline-flex items-center gap-2">
            <span class="text-xl font-black text-white tracking-tight">Startline</span>
            <span class="w-1.5 h-1.5 rounded-full bg-emerald-400 mb-0.5" />
          </RouterLink>
          <!-- Platform links (desktop only) -->
          <nav class="hidden lg:flex items-center gap-4 ml-4">
            <RouterLink
              to="/organizer/docs"
              class="text-sm text-slate-300 hover:text-white transition-colors"
            >
              {{ t('nav.documentation') }}
            </RouterLink>
            <RouterLink
              to="/organizer/support"
              class="text-sm text-slate-300 hover:text-white transition-colors inline-flex items-center gap-1.5"
            >
              {{ t('nav.support') }}
              <span
                v-if="hasUnread"
                class="w-1.5 h-1.5 rounded-full bg-emerald-400"
                :aria-label="t('support.nav.unread')"
              />
            </RouterLink>
            <RouterLink
              to="/organizer/about"
              class="text-sm text-slate-300 hover:text-white transition-colors"
            >
              {{ t('nav.about_startline') }}
            </RouterLink>
          </nav>
        </div>
        <!-- Profile menu (mobile only) -->
        <OrganizerProfileMenu :balance="balance" :loading="balanceLoading" />
        <!-- Inline controls (desktop only) -->
        <div class="hidden lg:flex items-center gap-3">
          <!-- Credit chip — always-visible credit balance linking to Credits page -->
          <CreditChip :balance="balance" :loading="balanceLoading" />
          <!-- Language selector -->
          <div class="relative">
            <button
              type="button"
              class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-3 py-2 text-sm transition-colors flex items-center gap-2"
              @click="isLangMenuOpen = !isLangMenuOpen"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
              <span class="uppercase">{{ currentLocale }}</span>
            </button>
            <div
              v-if="isLangMenuOpen"
              class="absolute right-0 mt-2 w-40 rounded-lg bg-slate-800 border border-slate-700 shadow-xl py-1 z-20"
            >
              <button
                v-for="locale in supportedLocales"
                :key="locale"
                type="button"
                class="w-full text-left px-4 py-2 text-sm transition-colors"
                :class="[
                  currentLocale === locale
                    ? 'bg-slate-700 text-white font-semibold'
                    : 'text-slate-300 hover:bg-slate-750 hover:text-white',
                ]"
                @click="handleLanguageChange(locale)"
              >
                {{ t(`languages.${locale}`) }}
              </button>
            </div>
          </div>
          <!-- Logout button -->
          <button
            type="button"
            class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
            @click="handleLogout"
          >
            {{ t('auth.logout.button') }}
          </button>
        </div>
      </div>
    </div>
  </header>
</template>
